/**
 * The whole pipeline from a parsed link to a plan (design §5).
 *
 * Each step is a library call; this file only sequences them, applies the
 * app's cache and rate limits, and reports progress.
 */

import { searchCorridor, type CorridorChurch } from "@viamissa/corridor";
import type { LatLng } from "@viamissa/geo";
import { geocodeFirst } from "@viamissa/geocoding";
import type { Avoid, Stop as LinkStop, WallTime } from "@viamissa/gmaps-link";
import { HttpError } from "@viamissa/http";
import { fetchChurchPage, RELAY, UPSTREAM, type ChurchPage } from "@viamissa/masstimes";
import {
  ARRIVE_EARLY_MIN,
  directPlan,
  drivingWindow,
  missedDays,
  NONSTOP,
  planTrip,
  rankSingleMass,
  type Candidate,
  type Constraint,
  type DriveClock,
  type Plan,
  type TripInput,
} from "@viamissa/planner";
import { fetchDetour, fetchRoute, type Route } from "@viamissa/routing";
import { expandChurch, isHolyDay, type Event } from "@viamissa/schedule";
import { dateKeyAt, dateRange, DAY, HOUR, instantOf, MINUTE, timeZoneAt, weekdayOf, type DateKey } from "@viamissa/time";
import { cache, geocodingHttp, masstimesHttp, routingHttp, TTL } from "./runtime";

export type Place = { label: string; point: LatLng };

export type TimeChoice = { kind: "now" } | { kind: "depart"; wall: WallTime } | { kind: "arrive"; wall: WallTime };

/**
 * On a trip of more than a day: Mass on Sundays and holy days of obligation
 * only, or every day. A one-day trip always looks for any Mass that day.
 */
export type MassChoice = "obligation" | "daily";

export type TripSettings = {
  places: Place[];
  via: LatLng[];
  avoid: Avoid;
  time: TimeChoice;
  masses: MassChoice;
  /** Half-width of the search corridor, km. */
  corridorKm: number;
  /** Ignore churches farther than this off the route, minutes each way. */
  maxDetourMin: number;
  /** Extra minutes on every drive. */
  marginMin: number;
  /** Drive only between these local hours, or straight through. */
  drivingHours: { startHour: number; endHour: number } | null;
};

export type TripResult = {
  settings: TripSettings;
  route: Route;
  originTz: string;
  destinationTz: string;
  constraint: Constraint;
  clock: DriveClock;
  churches: CorridorChurch[];
  /** Every dated service per church id, Masses and others. */
  events: Map<string, Event[]>;
  timeZones: Map<string, string>;
  candidates: Candidate[];
  plan: Plan;
  /** Single-Mass alternatives, best first, one per church. */
  options: Plan[];
  /**
   * When nothing fits: the Masses along the route closest to the chosen time,
   * each as a plan that leaves just in time for it. Empty when a Mass fits.
   */
  nearby: Plan[];
  /** A trip of more than a day with no Sunday or holy day on it, so weekday Masses are shown. */
  weekdayFallback: boolean;
  direct: Plan;
  requiredDays: DateKey[];
  missed: DateKey[];
  narrowed: { fromKm: number; toKm: number; halfWidthKm: number }[];
};

export type Progress = (step: string, fraction?: number) => void;

export const DEFAULT_SETTINGS: Omit<TripSettings, "places" | "via" | "avoid" | "time"> = {
  masses: "obligation",
  corridorKm: 8,
  maxDetourMin: 20,
  marginMin: 0,
  drivingHours: null,
};

function currentPosition(): Promise<LatLng> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) return reject(new Error("This browser cannot share its location."));
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => reject(new Error("Location permission was refused. Type a starting point instead.")),
      { timeout: 15_000 },
    );
  });
}

/** Give every stop coordinates: from the link, the device, or the geocoder. */
export async function resolvePlaces(stops: LinkStop[]): Promise<Place[]> {
  const places: Place[] = [];
  for (const stop of stops) {
    if (stop.point) {
      places.push({ label: stop.label, point: stop.point });
      continue;
    }
    if (stop.currentLocation) {
      places.push({ label: "Your location", point: await currentPosition() });
      continue;
    }
    const near = places[places.length - 1]?.point;
    const found = await cache.wrap(`geocode:${stop.label.toLowerCase()}`, TTL.geocode, () =>
      geocodeFirst({ query: stop.label, near, http: geocodingHttp }),
    );
    if (!found) throw new Error(`Couldn't find "${stop.label}" on the map. Try a fuller address.`);
    places.push({ label: stop.label, point: { lat: found.lat, lng: found.lng } });
  }
  return places;
}

const roundKey = (point: LatLng) => `${point.lat.toFixed(3)},${point.lng.toFixed(3)}`;

/**
 * masstimes.org is called directly from the browser. If that ever fails at the
 * network level (CORS refused, blocked host), the rest of the session goes
 * through the site's own relay instead; an HTTP error from upstream is a real
 * error and is not retried that way.
 */
let masstimesEndpoint = UPSTREAM;

function fetchPage(point: LatLng, page: number): Promise<ChurchPage> {
  return cache.wrap(`churches:${roundKey(point)}:${page}`, TTL.churches, async () => {
    const query = { lat: point.lat, lng: point.lng, page, http: masstimesHttp };
    try {
      return await fetchChurchPage({ ...query, endpoint: masstimesEndpoint });
    } catch (error) {
      if (masstimesEndpoint === RELAY || error instanceof HttpError) throw error;
      masstimesEndpoint = RELAY;
      return fetchChurchPage({ ...query, endpoint: RELAY });
    }
  });
}

function routeStops(settings: TripSettings): LatLng[] {
  const points = settings.places.map((place) => place.point);
  // A dragged route on a two-stop trip: go through the via points in order.
  if (points.length === 2 && settings.via.length) return [points[0]!, ...settings.via, points[1]!];
  return points;
}

/** Days the plan should try to cover between departure and arrival. */
function requiredDays(masses: MassChoice, first: DateKey, last: DateKey): DateKey[] {
  const days = dateRange(first, last);
  return masses === "daily" ? days : days.filter((day) => weekdayOf(day) === 0 || isHolyDay(day));
}

function toCandidates(churches: CorridorChurch[], events: Map<string, Event[]>, settings: TripSettings, masses: MassChoice): Candidate[] {
  const out: Candidate[] = [];
  for (const entry of churches) {
    if (entry.toMin > settings.maxDetourMin) continue;
    for (const event of events.get(entry.church.id) ?? []) {
      if (event.category !== "mass") continue;
      const dayKey = masses === "obligation" ? event.obligation : event.date;
      if (!dayKey) continue;
      out.push({
        id: event.id,
        churchId: entry.church.id,
        start: event.start,
        minutes: event.minutes,
        dayKey,
        alongMin: entry.join.alongMin,
        toMin: entry.toMin,
        backMin: entry.backMin,
      });
    }
  }
  return out;
}

/** Best option per church, so the alternatives are different places, not the same one at 9 and 11. */
function onePerChurch(plans: Plan[], limit: number): Plan[] {
  const seen = new Set<string>();
  const out: Plan[] = [];
  for (const plan of plans) {
    const church = plan.stops[0]?.candidate.churchId;
    if (!church || seen.has(church)) continue;
    seen.add(church);
    out.push(plan);
    if (out.length === limit) break;
  }
  return out;
}

function plansFor(candidates: Candidate[], input: TripInput) {
  return { plan: planTrip(candidates, input), options: onePerChurch(rankSingleMass(candidates, input), 6) };
}

/**
 * Depart at: a first Mass that means waiting longer than this is offered as
 * "leave later" in the closest times instead of being planned as a long wait.
 */
const MAX_WAIT_MIN = 90;

/** How far either side of the chosen time to look for the closest Masses when none fits. */
const NEARBY_DAYS = 3;

/**
 * The Masses closest to the chosen time, for when none fits: each one planned
 * as a trip that leaves just in time for it. Up to `before` that mean leaving
 * earlier (by at most half a day) and `after` that mean leaving later, one per
 * church on each side, in departure order. Never suggests leaving in the past.
 */
export function nearbyPlans(candidates: Candidate[], input: TripInput, wantedDepart: number, now: number, before = 2, after = 3): Plan[] {
  const clock = input.clock ?? NONSTOP;
  const margin = input.marginMin ?? 0;
  const plans = candidates
    .map((candidate) => {
      const depart = clock.backward(candidate.start - ARRIVE_EARLY_MIN * MINUTE, candidate.alongMin + candidate.toMin + margin);
      return depart >= now ? rankSingleMass([candidate], { ...input, constraint: { kind: "depart", at: depart } })[0] : undefined;
    })
    .filter((plan): plan is Plan => plan !== undefined);
  const earlier = plans.filter((plan) => plan.depart < wantedDepart && plan.depart >= wantedDepart - 12 * HOUR).sort((a, b) => b.depart - a.depart);
  const later = plans.filter((plan) => plan.depart >= wantedDepart).sort((a, b) => a.depart - b.depart);
  return [...onePerChurch(earlier, before).reverse(), ...onePerChurch(later, after)];
}

/** Run the whole pipeline. */
export async function runTrip(settings: TripSettings, onProgress: Progress, signal?: AbortSignal): Promise<TripResult> {
  onProgress("Finding the route");
  const stops = routeStops(settings);
  const route = await cache.wrap(
    `route:${stops.map(roundKey).join(";")}:${JSON.stringify(settings.avoid)}`,
    TTL.route,
    () => fetchRoute({ stops, avoid: settings.avoid, http: routingHttp }),
  );

  const origin = settings.places[0]!.point;
  const destination = settings.places[settings.places.length - 1]!.point;
  const originTz = timeZoneAt(origin.lat, origin.lng);
  const destinationTz = timeZoneAt(destination.lat, destination.lng);
  const clock = settings.drivingHours ? drivingWindow({ ...settings.drivingHours, timeZone: originTz }) : NONSTOP;

  const time = settings.time;
  const constraint: Constraint =
    time.kind === "arrive"
      ? { kind: "arrive", by: instantOf(time.wall, destinationTz) }
      : { kind: "depart", at: time.kind === "depart" ? instantOf(time.wall, originTz) : Date.now() };

  const base: TripInput = { totalMin: route.totalMin, constraint, marginMin: settings.marginMin, clock, maxWaitMin: MAX_WAIT_MIN };
  const direct = directPlan(base);
  // Long enough for Masses to push the trip later, without expanding a week of schedules.
  const from = constraint.kind === "depart" ? constraint.at : clock.backward(constraint.by, route.totalMin + 12 * 60);
  const to = constraint.kind === "depart" ? clock.forward(constraint.at, route.totalMin + 12 * 60) : constraint.by;
  // Schedules are expanded wider than that, to suggest the closest times when nothing fits.
  const wideFrom = Math.min(from, direct.depart - NEARBY_DAYS * DAY);
  const wideTo = Math.max(to, direct.depart + NEARBY_DAYS * DAY);

  const tripDays = requiredDays(settings.masses, dateKeyAt(direct.depart, originTz), dateKeyAt(direct.finish, destinationTz));

  onProgress("Looking for churches along the way", 0);
  const corridor = await searchCorridor({
    route,
    halfWidthKm: settings.corridorKm,
    fetchPage,
    signal,
    onProgress: (done, found) => onProgress(`Looking for churches along the way (${found} so far)`, done),
  });

  onProgress("Reading Mass times");
  const events = new Map<string, Event[]>();
  const timeZones = new Map<string, string>();
  for (const { church } of corridor.churches) {
    const timeZone = timeZoneAt(church.lat, church.lng);
    timeZones.set(church.id, timeZone);
    events.set(church.id, expandChurch({ church, timeZone, from: wideFrom, to: wideTo }));
  }

  onProgress("Fitting Mass into the trip");
  const inWindow = (candidate: Candidate) => candidate.start >= from && candidate.start < to;
  // A one-day trip looks for any Mass that day. A longer one asked for Sundays and holy
  // days, with neither and no vigil Mass to catch, offers weekday Masses rather than nothing.
  const oneDay = dateKeyAt(direct.depart, originTz) === dateKeyAt(direct.finish, destinationTz);
  const weekdayFallback =
    settings.masses === "obligation" &&
    !oneDay &&
    tripDays.length === 0 &&
    !toCandidates(corridor.churches, events, settings, "obligation").some(inWindow);
  const masses: MassChoice = oneDay || weekdayFallback ? "daily" : settings.masses;
  let all = toCandidates(corridor.churches, events, settings, masses);
  let candidates = all.filter(inWindow);
  let { plan, options } = plansFor(candidates, base);
  const now = Date.now();
  let nearby = plan.stops.length ? [] : nearbyPlans(all, base, direct.depart, now);

  // Replace the pessimistic detour estimates with real routes for the churches we'd show.
  const shown = new Set(
    [...plan.stops, ...[...options, ...nearby].flatMap((option) => option.stops)].map((stop) => stop.candidate.churchId),
  );
  const toRefine = corridor.churches.filter((entry) => shown.has(entry.church.id) && !entry.exact);
  for (const [index, entry] of toRefine.entries()) {
    if (signal?.aborted) break;
    onProgress("Checking detours", index / toRefine.length);
    const detour = await cache.wrap(
      `detour:${roundKey(entry.join.point)}:${entry.church.id}:${JSON.stringify(settings.avoid)}`,
      TTL.detour,
      () => fetchDetour(entry.join.point, entry.church, { avoid: settings.avoid, http: routingHttp }),
    );
    if (detour) Object.assign(entry, { toMin: detour.toMin, backMin: detour.backMin, exact: true });
  }
  if (toRefine.length) {
    all = toCandidates(corridor.churches, events, settings, masses);
    candidates = all.filter(inWindow);
    ({ plan, options } = plansFor(candidates, base));
    nearby = plan.stops.length ? [] : nearbyPlans(all, base, direct.depart, now);
  }

  const firstDay = dateKeyAt(plan.depart, originTz);
  const lastDay = dateKeyAt(Math.max(plan.finish, direct.finish), destinationTz);
  const required = weekdayFallback ? [] : requiredDays(settings.masses, firstDay, lastDay);

  return {
    settings,
    route,
    originTz,
    destinationTz,
    constraint,
    clock,
    churches: corridor.churches,
    events,
    timeZones,
    candidates,
    plan,
    options,
    nearby,
    weekdayFallback,
    direct,
    requiredDays: required,
    missed: missedDays(plan, required),
    narrowed: corridor.narrowed,
  };
}

/** When the traveller drives past a church, with the trip's departure and clock. */
export function passTime(result: TripResult, entry: CorridorChurch): number {
  return result.clock.forward(result.plan.depart, entry.join.alongMin);
}

export const HOUR_MS = HOUR;

/**
 * When the traveller reaches each of their own waypoints on `plan`: drive on from
 * the last Mass before it (or from the start) to where the waypoint sits on the route.
 */
export function waypointTimes(result: TripResult, plan: Plan): number[] {
  const { route } = result;
  return route.stopIndices.slice(1, -1).map((pointIndex) => {
    const at = route.cumMin[pointIndex] ?? 0;
    let from = plan.depart;
    let fromMin = 0;
    for (const stop of plan.stops) {
      if (stop.candidate.alongMin > at) break;
      from = result.clock.forward(stop.leave, stop.candidate.backMin);
      fromMin = stop.candidate.alongMin;
    }
    return result.clock.forward(from, at - fromMin);
  });
}
